import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../../app/AppContext';
import { callFn } from '../../lib/api';
import { OrderCard } from '../../components/OrderCard';
import { PauseControl } from '../../components/PauseControl';
import { ALLOWED_TRANSITIONS, STATUS_LABEL, money } from '../../lib/types';
import type { Order, OrderStatus } from '../../lib/types';

type Tab = 'new' | 'active' | 'ready' | 'done' | 'cancelled';
const TABS: { key: Tab; label: string; statuses: OrderStatus[] }[] = [
  { key: 'new', label: 'New', statuses: ['pending'] },
  { key: 'active', label: 'In progress', statuses: ['confirmed', 'preparing'] },
  { key: 'ready', label: 'Ready', statuses: ['ready', 'out_for_delivery'] },
  { key: 'done', label: 'Done', statuses: ['picked_up', 'delivered'] },
  { key: 'cancelled', label: 'Cancelled', statuses: ['cancelled'] },
];

export function AdminOrders() {
  const { toast, me, reload } = useApp();
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [tab, setTab] = useState<Tab>('new');
  const [prompt, setPrompt] = useState<{ kind: 'confirm' | 'cancel' | 'late' | 'refund'; order: Order } | null>(null);

  const load = useCallback(async () => {
    try { const r = await callFn<{ orders: Order[] }>('list_orders'); setOrders(r.orders); }
    catch (e) { toast(e instanceof Error ? e.message : 'Could not load orders.', 'err'); }
  }, [toast]);
  useEffect(() => { void load(); const t = setInterval(() => void load(), 15000); return () => clearInterval(t); }, [load]);

  const counts = useMemo(() => {
    const c: Record<Tab, number> = { new: 0, active: 0, ready: 0, done: 0, cancelled: 0 };
    for (const o of orders || []) { const t = TABS.find((x) => x.statuses.includes(o.status)); if (t) c[t.key]++; }
    return c;
  }, [orders]);

  const shown = (orders || []).filter((o) => TABS.find((t) => t.key === tab)!.statuses.includes(o.status));

  const act = async (label: string, fn: () => Promise<unknown>) => {
    try { await fn(); toast(label, 'ok'); setPrompt(null); await load(); }
    catch (e) { toast(e instanceof Error ? e.message : 'That did not work.', 'err'); }
  };
  const setStatus = (o: Order, status: OrderStatus, extra: Record<string, unknown> = {}) =>
    act(`Order ${STATUS_LABEL[status].toLowerCase()}`, () => callFn('owner_set_order_status', { orderId: o.id, status, ...extra }));

  const actionsFor = (o: Order) => {
    const next = ALLOWED_TRANSITIONS[o.status] || [];
    const list: { label: string; kind?: 'primary' | 'ghost' | 'danger'; onClick: () => void }[] = [];
    if (o.status === 'pending') list.push({ label: 'Confirm', onClick: () => setPrompt({ kind: 'confirm', order: o }) });
    if (next.includes('preparing')) list.push({ label: 'Preparing', onClick: () => void setStatus(o, 'preparing') });
    if (next.includes('ready')) list.push({ label: 'Ready', onClick: () => void setStatus(o, 'ready') });
    if (next.includes('out_for_delivery') && o.fulfillment === 'delivery') list.push({ label: 'Out for delivery', onClick: () => void setStatus(o, 'out_for_delivery') });
    if (next.includes('picked_up') && o.fulfillment === 'pickup') list.push({ label: 'Picked up', onClick: () => void setStatus(o, 'picked_up') });
    if (next.includes('delivered') && o.fulfillment === 'delivery') list.push({ label: 'Delivered', onClick: () => void setStatus(o, 'delivered') });
    if (['confirmed', 'preparing', 'ready', 'out_for_delivery'].includes(o.status) && o.etaAt) list.push({ label: 'Running late', kind: 'ghost', onClick: () => setPrompt({ kind: 'late', order: o }) });
    if (o.paymentMethod === 'pay_at_store' && o.paymentStatus === 'unpaid' && o.status !== 'cancelled') list.push({ label: 'Mark paid', kind: 'ghost', onClick: () => void act('Marked paid', () => callFn('owner_mark_order_paid', { orderId: o.id })) });
    if (o.paymentMethod === 'online' && o.paymentStatus === 'paid') list.push({ label: 'Refund', kind: 'ghost', onClick: () => setPrompt({ kind: 'refund', order: o }) });
    if (next.includes('cancelled')) list.push({ label: 'Cancel', kind: 'danger', onClick: () => setPrompt({ kind: 'cancel', order: o }) });
    return list;
  };

  return (
    <div className="admin-orders">
      {/* Pausing belongs here as well as in Settings: this is the screen an
          owner is looking at when the kitchen gets slammed. */}
      {me && (
        <PauseControl channel="all" noun="all ordering" pause={me.pauses?.all}
                      placeholder="We're slammed — back in 30 minutes."
                      timezone={me.timezone} toast={toast} onDone={reload} />
      )}
      <div className="chips" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} className={`chip ${tab === t.key ? 'chip-on' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}{counts[t.key] > 0 && <span className="badge">{counts[t.key]}</span>}
          </button>
        ))}
        <button className="chip" onClick={() => void load()} aria-label="Refresh">↻</button>
      </div>
      {!orders && <p className="muted">Loading…</p>}
      {orders && shown.length === 0 && <p className="muted">Nothing here.</p>}
      <div className="order-list">
        {shown.map((o) => <OrderCard showCustomer key={o.id} order={o} actions={actionsFor(o)} />)}
      </div>
      {prompt && <Prompt {...prompt} onClose={() => setPrompt(null)} onConfirm={(v) => {
        const o = prompt.order;
        if (prompt.kind === 'confirm') return setStatus(o, 'confirmed', { etaMinutes: v.minutes });
        if (prompt.kind === 'cancel') return setStatus(o, 'cancelled', { reason: v.text });
        if (prompt.kind === 'late') return act('ETA updated', () => callFn('owner_adjust_eta', { orderId: o.id, addMinutes: v.minutes }));
        return act('Refund issued', () => callFn('owner_refund_order', { orderId: o.id, ...(v.cents ? { amountCents: v.cents } : {}) }));
      }} />}
    </div>
  );
}

function Prompt({ kind, order, onClose, onConfirm }: { kind: 'confirm' | 'cancel' | 'late' | 'refund'; order: Order; onClose: () => void; onConfirm: (v: { minutes: number; text: string; cents: number }) => Promise<unknown> | void }) {
  const [minutes, setMinutes] = useState(kind === 'confirm' ? 20 : 10);
  const [text, setText] = useState('');
  const [dollars, setDollars] = useState(((order.totalCents - (order.refundedCents || 0)) / 100).toFixed(2));
  const titles = { confirm: 'Confirm order', cancel: 'Cancel order', late: 'Running late', refund: 'Refund' };
  const remaining = order.totalCents - (order.refundedCents || 0);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-body">
          <h3>{titles[kind]}</h3>
          {kind === 'confirm' && <label>Ready in (minutes)<input className="input" type="number" min={5} max={240} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} /></label>}
          {kind === 'late' && <label>Add minutes<input className="input" type="number" min={5} max={120} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} /></label>}
          {kind === 'cancel' && <label>Reason (shown to the customer)<input className="input" maxLength={200} value={text} onChange={(e) => setText(e.target.value)} /></label>}
          {kind === 'refund' && <label>Amount (up to {money(remaining, order.currency)})<input className="input" type="number" step="0.01" min={0.01} max={remaining / 100} value={dollars} onChange={(e) => setDollars(e.target.value)} /></label>}
          <div className="row between" style={{ marginTop: 16 }}>
            <button className="btn btn-ghost" onClick={onClose}>Back</button>
            <button className={`btn ${kind === 'cancel' ? 'btn-danger' : ''}`} disabled={kind === 'cancel' && !text.trim()} onClick={() => void onConfirm({ minutes, text: text.trim(), cents: Math.round(Number(dollars) * 100) })}>{titles[kind]}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
